import {
  Button,
  Card,
  SimpleGrid,
  Stack,
  TextInput,
  Title,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { IconCalendar } from '@tabler/icons-react'
import { RichTextEditor } from 'components/RichTextEditor'
import {
  InternalGroupSelect,
  UserMultiSelect,
  UserSelect,
} from 'components/Select'
import { SummaryNode } from 'modules/summaries/types'
import { useState } from 'react'
import { CustomInputLabel } from './CustomInputLabel'
import { useSummaryFormAPI } from './useSummaryFormAPI'
import { useSummaryLogic } from './useSummaryLogic'

interface SummaryFormProps {
  summary?: SummaryNode
  onCompletedCallback: () => void
}

export const SummaryForm: React.FC<SummaryFormProps> = ({
  summary,
  onCompletedCallback,
}) => {
  const { form, editor, onSubmit } = useSummaryLogic({
    ...useSummaryFormAPI(summary, onCompletedCallback),
  })

  const { formState, register, handleSubmit, getValues, setValue, watch } = form
  const { errors, isSubmitting } = formState
  const [reporter, setReporter] = useState<string>(getValues('reporter'))
  const [internalGroup, setInternalGroup] = useState<string>(
    getValues('internalGroup') ?? ''
  )
  const [participants, setParticipants] = useState<string[]>(
    getValues('participants')
  )

  function handleParticipantsCallback(values: string[]) {
    setParticipants(values)
    setValue('participants', values)
  }
  function handleInternalGroupCallback(value: string) {
    setInternalGroup(value)
    setValue('internalGroup', value)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack>
        <Card withBorder style={{ overflow: 'visible' }}>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Stack>
              <CustomInputLabel label={'Referent'}>
                <UserSelect
                  userId={reporter}
                  setUserCallback={value => {
                    setReporter(value)
                    setValue('reporter', value)
                  }}
                />
              </CustomInputLabel>
              <CustomInputLabel label={'Interngjeng'}>
                <InternalGroupSelect
                  withOtherOption
                  internalGroupId={internalGroup}
                  setInternalGroupCallback={handleInternalGroupCallback}
                />
              </CustomInputLabel>
              <CustomInputLabel
                label={'Tittel'}
                description={"Trengs kun hvis du har valgt 'Annet'"}
              >
                <TextInput
                  {...register('title')}
                  disabled={!(watch('internalGroup') === 'other')}
                />
              </CustomInputLabel>
              <CustomInputLabel label={'Dato'}>
                <DateInput
                  placeholder="Velg en dato"
                  leftSection={<IconCalendar size={14} />}
                  error={errors?.date?.message}
                  defaultValue={getValues('date') ?? undefined}
                  onChange={date => setValue('date', date)}
                />
              </CustomInputLabel>
            </Stack>
            <UserMultiSelect
              label={
                <Title mb={'xs'} order={5} c={'dimmed'}>
                  Deltakere
                </Title>
              }
              users={participants}
              setUsersCallback={handleParticipantsCallback}
            />
          </SimpleGrid>
        </Card>
        <RichTextEditor editor={editor} />
        <Button color={'samfundet-red'} disabled={isSubmitting} type={'submit'}>
          Lagre
        </Button>
      </Stack>
    </form>
  )
}
